import React, { Suspense } from 'react'
import { useLocation } from 'react-router'
import { useMount } from 'react-use'

import AppHeader from '@/components/layouts/DashboardLayout/components/DashboardSidebar/components/AppHeader'
import DashboardSidebar from '@/components/layouts/DashboardLayout/components/DashboardSidebar/DashboardSidebar'
import DashboardSidebarProvider from '@/components/layouts/DashboardLayout/components/DashboardSidebar/DashboardSidebarProvider'
import Footer from '@/components/layouts/DashboardLayout/components/Footer'
import EditInvitedCompanyNameModal from '@/components/misc/EditInvitedCompanyNameButton/EditInvitedCompanyNameModal/EditInvitedCompanyNameModal'
import PageContent from '@/components/misc/PageContent'
import SentryErrorBoundary from '@/components/misc/SentryErrorBoundary'
import { SidebarInset } from '@/components/ui/sidebar'
import LoadingWidget from '@/components/widgets/LoadingWidget'
import useAppDispatch from '@/hooks/useAppDispatch'
import { useAppStateQuery } from '@/hooks/useAppStateQuery'
import { useGroupDashboardSidebarLinks } from '@/hooks/useGroupDashboardSidebarLinks'
import { matchesPaths } from '@/lib/helpers/routeHelpers'
import { ROUTES } from '@/lib/routes'
import { LAYOUTS, setLayout } from '@/redux/slices/layout'

interface Props {
  children: React.ReactNode
  padded?: boolean
}

const GroupDashboardLayout: React.FC<Props> = ({ children, padded = true }) => {
  const location = useLocation()
  const sidebarLinks = useGroupDashboardSidebarLinks()
  const dispatch = useAppDispatch()

  useMount(() => {
    dispatch(setLayout(LAYOUTS.groupDashboard))
  })

  const appStateQuery = useAppStateQuery()

  if (appStateQuery.isLoading) {
    return <LoadingWidget />
  }

  // Some routes have dedicated loading skeletons, so prefer to show them
  // instead of the default loading widget
  function renderLoadingFallback() {
    const routesWithOwnFallback = [ROUTES.referrals()]

    return matchesPaths(location.pathname, routesWithOwnFallback) ? null : (
      <LoadingWidget />
    )
  }

  return (
    <>
      <DashboardSidebarProvider>
        <DashboardSidebar links={sidebarLinks} />

        <SidebarInset className="min-w-0">
          <AppHeader />

          <PageContent padded={padded}>
            <SentryErrorBoundary key={location.pathname}>
              <Suspense fallback={renderLoadingFallback()}>{children}</Suspense>
            </SentryErrorBoundary>
          </PageContent>

          <EditInvitedCompanyNameModal />
          <Footer />
        </SidebarInset>
      </DashboardSidebarProvider>
    </>
  )
}

export default GroupDashboardLayout
